/**
 * useDueBannerItems — the data behind <UpcomingDueBanner>: payroll timecard
 * deadlines, admin-configured Deadline entries with their dashboard banner
 * turned on, and the logged-in user's own incomplete personal reminders.
 *
 * Pulled out of DashboardPage.js into a shared hook so DashboardLayout.js
 * (the persistent chrome around every authenticated route) can render the
 * banner globally -- "wherever they are in the app" -- instead of only on
 * the Dashboard page itself.
 */
import { useState, useEffect } from 'react';
import { api } from '../lib/api';

export default function useDueBannerItems() {
  const [payrollDeadlines, setPayrollDeadlines] = useState([]);
  const [deadlineBanners, setDeadlineBanners] = useState([]);
  const [personalDueDates, setPersonalDueDates] = useState([]);

  useEffect(() => {
    api.get('/attendance/calendar-entries/?page_size=500')
      .then((res) => {
        const entries = res.data?.results || res.data || [];
        setPayrollDeadlines(entries.filter((e) => e.event_type === 'payroll_deadline'));
        setDeadlineBanners(entries.filter((e) => e.event_type === 'deadline' && e.banner_enabled));
      })
      .catch(() => { setPayrollDeadlines([]); setDeadlineBanners([]); });

    api.get('/attendance/my-due-dates/?page_size=100')
      .then((res) => setPersonalDueDates(res.data?.results || res.data || []))
      .catch(() => setPersonalDueDates([]));
  }, []);

  return [
    // e.date is a plain "YYYY-MM-DD" string -- append a local midnight time
    // so it parses as local midnight of the due date, not UTC midnight
    // (new Date('2026-06-30') parses as UTC, which shifts the day back for
    // any timezone west of UTC). A 24h window then reads naturally as
    // "shows all day on the day before it's due."
    ...payrollDeadlines.map((e) => ({
      key: `payroll-${e.id}`, label: e.label, dueAt: `${e.date}T00:00:00`, hoursBefore: 24,
    })),
    // Admin-configured Deadline entries -- only ones with the dashboard
    // banner explicitly turned on. bannerText/showCountdown come straight
    // from the admin's own configuration; hoursBefore uses the same
    // notify_threshold_hours that drives the real backend notification, so
    // the on-screen banner and the actual notification agree on timing.
    ...deadlineBanners.map((e) => ({
      key: `deadline-${e.id}`, label: e.label, bannerText: e.banner_text, showCountdown: e.banner_countdown_enabled,
      // due_time (if the admin set one) narrows "due sometime on this date"
      // down to an actual time, e.g. 5:00 PM instead of midnight.
      dueAt: `${e.date}T${e.due_time || '00:00:00'}`, hoursBefore: e.notify_threshold_hours || 24,
    })),
    ...personalDueDates
      .filter((d) => !d.completed)
      .map((d) => ({
        key: `personal-${d.id}`, label: d.title, dueAt: d.due_date, hoursBefore: d.reminder_hours_before,
      })),
  ];
}
